import React, { useState } from 'react';
import { Grid, Typography, Box, Card, CardContent, ListItem, useMediaQuery, IconButton } from '@mui/material';
import { useUpdateProfileMutation, useAddProfileDataMutation, useFetchProfileQuery } from '../profileApiSlice';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../Auth/authSlice';
import { variants } from '../../../variants/variants';
import {
  RenderCard,
  SkillRenderCard,
  ProfileCard,
  ContactCard,
  LanguagesCard,
  EditDialog,
  AddDataDialog,
  EditSkillsDialog,
} from './AboutMeComponents';
import { WorkspacePremiumOutlined,Edit as EditIcon } from '@mui/icons-material';
import ProfileSkeleton from './ProfileSkeleton';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTheme } from '@emotion/react';

const AboutMe = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(theme.breakpoints.down('md'));
  const isLg = useMediaQuery(theme.breakpoints.down('lg'));
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const isCurrentUser = true;
  const { empCode } = useParams();
  const userr = useSelector(selectCurrentUser);
  const [updateProfile] = useUpdateProfileMutation();
  const [addProfileData] = useAddProfileDataMutation();
  const { data: profileData, isLoading, error } = useFetchProfileQuery(empCode || userr?.empCode);
  const [showAll, setShowAll] = useState({});
  const [editData, setEditData] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [addData, setAddData] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [openSkillsDialog, setOpenSkillsDialog] = useState(false);

  
  if (isLoading) {
    return <ProfileSkeleton />;
  }
  
  if (error) return <div>Error loading profile</div>;
  
  const user = profileData ? profileData : null;
  if (!user) return <div>User not found</div>;
  const profileDetails = {
    name: user?.userName,
    email: user?.email,
    summary: user?.summary,
    officeLocation: user?.officeLocation,
  };

  const handleShowAllToggle = key => {
    setShowAll(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleEditClick = (sectionTitle, sectionData) => {
    setDialogTitle(`Edit ${sectionTitle}`);
    setEditData(sectionData);
    setOpenDialog(true);
  };

  const handleSkillEditClick = (sectionTitle, sectionData) => {
    setDialogTitle(sectionTitle);
    setEditData({ skills: sectionData });
    setOpenSkillsDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditData(null);
  };

  const handleSave = async () => {
    console.log('editData', editData)
    const keys = Object.keys(editData);
    for(let key of keys) {
      if (editData[key] == null || editData[key] == undefined || editData[key] == '') {
        toast.error(`Please fill all the fields in ${key}`);
      }
    }
    // await updateProfile({ empCode: user.empCode, data: editData });
    toast.success('Profile updated successfully!');
    setOpenDialog(false);
    setOpenSkillsDialog(false);
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    const [section, indexWithField] = name.split('[');
    const index = indexWithField ? indexWithField.split(']')[0] : undefined;
    const field = indexWithField ? indexWithField.split('].')[1] : undefined;

    if (openDialog) {
      setEditData(prevEditData => {
        if (index !== undefined && field !== undefined) {
          return {
            ...prevEditData,
            [section]: prevEditData[section]?.map((item, idx) =>
              idx.toString() === index ? { ...item, [field]: value } : item
            ),
          };
        } else if (index !== undefined) {
          return {
            ...prevEditData,
            [section]: prevEditData[section].map((item, idx) => (idx.toString() === index ? value : item)),
          };
        } else {
          return {
            ...prevEditData,
            [name]: value,
          };
        }
      });
    } else if (addDialogOpen) {
      setAddData(prevAddData => {
        if (index !== undefined && field !== undefined) {
          return {
            ...prevAddData,
            [section]: prevAddData[section].map((item, idx) =>
              idx.toString() === index ? { ...item, [field]: value } : item
            ),
          };
        } else if (index !== undefined) {
          return {
            ...prevAddData,
            [section]: prevAddData[section].map((item, idx) => (idx.toString() === index ? value : item)),
          };
        } else {
          return {
            ...prevAddData,
            [name]: value,
          };
        }
      });
    }
  };

  const formatDateString = dateString => {
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${date.getFullYear()}`;
  };

  const handleAddData = (sectionTitle, categoryIndex) => {
    const sectionKey = sectionTitle.charAt(0).toLowerCase() + sectionTitle.slice(1).replace(/\s/g, '');

    const emptyEntry = {};
    if (sectionKey === 'languages') {
      emptyEntry.language = '';
      emptyEntry.proficiency = '';
    } else if (sectionKey === 'projects') {
      emptyEntry.domain = '';
      emptyEntry.description = '';
      emptyEntry.technologies = '';
      emptyEntry.startDate = '';
      emptyEntry.endDate = '';
    } else if (sectionKey === 'contact') {
      emptyEntry.phoneNumber = '';
      emptyEntry.linkedinUrl = '';
      emptyEntry.personalWebsite = '';
      emptyEntry.skypeId = '';
    } else if (sectionKey === 'workHistory') {
      emptyEntry.company = '';
      emptyEntry.position = '';
      emptyEntry.startDate = '';
      emptyEntry.endDate = '';
    } else if (sectionKey === 'education') {
      emptyEntry.institution = '';
      emptyEntry.degree = '';
      emptyEntry.fieldOfStudy = '';
      emptyEntry.startDate = '';
      emptyEntry.endDate = '';
    } else if (sectionKey === 'certifications') {
      emptyEntry.name = '';
    }
    if (sectionKey === 'skills') {
      setEditData(prevData => {
        const newSkills = prevData.skills ? [...prevData.skills] : [];
        if (!newSkills[categoryIndex]) {
          newSkills[categoryIndex] = { category: '', skills: [] };
        }
        newSkills[categoryIndex] = {
          ...newSkills[categoryIndex],
          skills: [...newSkills[categoryIndex].skills, emptyEntry],
        };
        return { ...prevData, skills: newSkills };
      });
    } else {
      setDialogTitle(`Add ${sectionTitle}`);
      setAddData({ [sectionKey]: [emptyEntry] });
    }
    if (sectionKey !== 'skills') {
      setAddDialogOpen(true);
    }
  };

  const handleAddDialogClose = () => {
    setAddDialogOpen(false);
    setAddData(null);
  };

  const handleAddSave = async () => {
    const sectionKey = Object.keys(addData)[0];
    const newData = addData[sectionKey];
    const resposne = await addProfileData({ empCode: user?.empCode, sectionKey, newData });
    toast.success('Profile updated successfully!');
    setAddDialogOpen(false);
  };

  const handleDelete = (key, index) => {
    setEditData(prevEditData => {
      const updatedSection = prevEditData[key].filter((_, idx) => idx !== index);
      return { ...prevEditData, [key]: updatedSection };
    });
  };

  const handleSkillsDialogClose = () => {
    setOpenSkillsDialog(false);
    setEditData(null);
  };

  const handleSkillsInputChange = e => {
    const { name, value } = e.target;

    const parseName = name => {
      const sectionMatch = name.match(/^([^\[]+)\[(\d+)]\.skills\[(\d+)]\.(.+)$/);
      if (!sectionMatch) {
        throw new Error('Invalid name format');
      }
      const [, section, index, skillIndex, field] = sectionMatch;
      return { section, index, skillIndex, field };
    };

    const { section, index, skillIndex, field } = parseName(name);

    setEditData(prevEditData => {
      const updatedCategories = prevEditData[section].map((category, catIdx) => {
        if (catIdx.toString() === index) {
          const updatedSkills = category.skills.map((skill, skillIdx) => {
            if (skillIdx.toString() === skillIndex) {
              return { ...skill, [field]: value };
            }
            return skill;
          });
          return { ...category, skills: updatedSkills };
        }
        return category;
      });

      return {
        ...prevEditData,
        [section]: updatedCategories,
      };
    });
  };

  const handleSkillsDelete = (key, catIndex, skillIndex) => {
    setEditData(prevEditData => {
      const newEditData = JSON.parse(JSON.stringify(prevEditData));
      newEditData[key][catIndex].skills = newEditData[key][catIndex].skills.filter((_, idx) => idx !== skillIndex);
      return newEditData;
    });
  };

  const handleCertificationChange = value => {
    setEditData(prevData => ({
      ...prevData,
      certifications: value,
    }));
  };

  return (
    <div className='content' style={{ padding: 0 }}>
      <Box
        sx={{
          backgroundColor: isSm ? '' : '#EEF5FF',
          height: '100%',
          overflow: 'auto',
          borderRadius: variants.boxRadius.NAVBAR,
          border: isSm ? 0 : 1,
          borderColor: 'divider',
        }}
      >
        <Grid container spacing={2} sx={{ overflowY: 'auto', height: '92vh', padding: isSm ? 0 : 1 }}>
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: '8px', boxShadow: 'none', border: 1, borderColor: 'divider', minHeight: '45vh' }}>
              <CardContent>
                <Box display='flex' justifyContent='space-between' alignItems='center'>
                <Typography variant='h6' fontWeight='bold'>
                  About Me
                </Typography>
                <Typography>
                        {isCurrentUser && (
                          <IconButton onClick={() => handleEditClick('Profile', profileDetails)}>
                            <EditIcon />
                          </IconButton>
                        )}
                      </Typography>
                      </Box>
                <ProfileCard user={user} onEdit={handleEditClick} isCurrentUser={isCurrentUser} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <ContactCard
              contact={user?.contact}
              handleEditClick={handleEditClick}
              isCurrentUser={isCurrentUser}
              onAdd={() => handleAddData('Contact', user?.contact)}
            />
            <LanguagesCard
              languages={user?.languages}
              handleEditClick={handleEditClick}
              isCurrentUser={isCurrentUser}
              onAdd={() => handleAddData('Languages', { languages: user?.languages })}
            />
          </Grid>
          <Grid item xs={12}>
            <SkillRenderCard
              title='Skills'
              items={user?.skills}
              renderItem={skill => (
                <ListItem sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Typography>{skill.name}</Typography>
                  <Typography> Proficiency={skill.proficiency} </Typography>
                </ListItem>
              )}
              onEdit={() => handleSkillEditClick('Edit Skills', user?.skills)}
              onAdd={() => handleAddData('Skills', { skills: user?.skills })}
              isCurrentUser={isCurrentUser}
            />
          </Grid>
          <RenderCard
            title='Certifications'
            items={user?.certifications}
            showAll={showAll}
            showAllKey='certifications'
            sliceCount={4}
            renderItem={certification => (
              <Grid item xs={12} xl={12} md={12}>
                <Card variant='outlined'>
                  <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                    <WorkspacePremiumOutlined sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant='body1'>{certification}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
            handleShowAllToggle={handleShowAllToggle}
            onEdit={() => handleEditClick('Certifications', { certifications: user?.certifications })}
            isCurrentUser={isCurrentUser}
          />
          <RenderCard
            title='Projects'
            items={user?.projects}
            showAll={showAll}
            showAllKey='projects'
            sliceCount={1}
            renderItem={project => (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                <Typography variant='subtitle1' fontWeight='bold'>
                  {project.domain}
                </Typography>
                <Typography variant='body2'>{`Description: ${project.description}`}</Typography>
                <Typography variant='body2'>{`Technologies: ${project.technologies}`}</Typography>
                <Typography variant='body2'>{`Duration: ${formatDateString(project.startDate)} to ${formatDateString(
                  project.endDate
                )}`}</Typography>
              </Box>
            )}
            handleShowAllToggle={handleShowAllToggle}
            onEdit={() => handleEditClick('Projects', { projects: user?.projects })}
            isCurrentUser={isCurrentUser}
            onAdd={() => handleAddData('Projects', { projects: user?.projects })}
          />
          <RenderCard
            title='Education'
            items={user?.education}
            showAll={showAll}
            showAllKey='education'
            sliceCount={2}
            renderItem={edu => (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                <Typography variant='subtitle1' fontWeight='bold'>
                  {edu.institution}
                </Typography>
                <Typography variant='body2'>{`Degree: ${edu.degree}`}</Typography>
                <Typography variant='body2'>{`Field of study: ${edu.fieldOfStudy}`}</Typography>
                <Typography variant='body2'>{`Duration: ${formatDateString(edu.startDate)} to ${formatDateString(
                  edu.endDate
                )}`}</Typography>
              </Box>
            )}
            handleShowAllToggle={handleShowAllToggle}
            onEdit={() => handleEditClick('Education', { education: user?.education })}
            isCurrentUser={isCurrentUser}
            onAdd={() => handleAddData('Education', { education: user?.education })}
          />
          <RenderCard
            title='Work History'
            items={user?.workHistory}
            showAll={showAll}
            showAllKey='workHistory'
            sliceCount={1}
            renderItem={work => (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                <Typography variant='subtitle1' fontWeight='bold'>
                  {work?.company}
                </Typography>
                <Typography variant='body2'>{`Position: ${work.position}`}</Typography>
                <Typography variant='body2'>{`Duration: ${formatDateString(work.startDate)} to ${formatDateString(
                  work?.endDate
                )}`}</Typography>
              </Box>
            )}
            handleShowAllToggle={handleShowAllToggle}
            onEdit={() => handleEditClick('Work History', { workHistory: user?.workHistory })}
            isCurrentUser={isCurrentUser}
            onAdd={() => handleAddData('Work History', { workHistory: user?.workHistory })}
          />
        </Grid>
      </Box>
      <EditDialog
        open={openDialog}
        dialogTitle={dialogTitle}
        editData={editData}
        handleDialogClose={handleDialogClose}
        handleInputChange={handleInputChange}
        handleSave={handleSave}
        handleDelete={handleDelete}
        handleCertificationChange={handleCertificationChange}
      />
      <EditSkillsDialog
        open={openSkillsDialog}
        dialogTitle={dialogTitle}
        editData={editData?.skills}
        handleDialogClose={handleSkillsDialogClose}
        handleInputChange={handleSkillsInputChange}
        handleSave={handleSave}
        handleDelete={handleSkillsDelete}
        handleAddData={handleAddData}
        id={user?.empCode}
        setOpenDialog={setOpenDialog}
        setOpenSkillsDialog={setOpenSkillsDialog}
        setEditData={setEditData}
      />
      <AddDataDialog
        open={addDialogOpen}
        dialogTitle={dialogTitle}
        addData={addData}
        handleDialogClose={handleAddDialogClose}
        handleInputChange={handleInputChange}
        handleSave={handleAddSave}
      />
    </div>
  );
};

export default AboutMe;
